import React, { useEffect, useState } from "react";
import { AvatarChip } from "./AvatarChip";
import { useAuth } from "../context/AuthContext";
import {
  fetchTeamHq, createRoster, updateRoster, deleteRoster, fetchGames,
  updateTeamHq, promoteCaptain, demoteCaptain, kickMember,
  fetchRecruitment, createRecruitment, closeRecruitment,
  fetchTeamJoinRequests, decideInbox,
  fetchTeamAvailability, setMyAvailability,
} from "../lib/api";
import { TeamChat } from "./TeamChat";
import { TryoutsTab } from "./TryoutsTab";
import { SessionScheduleModal } from "./SessionScheduleModal";

/**
 * TeamHqModal — the "Headquarters" view for a team the current user is on.
 *
 * Visible to all members. CRUD on rosters is owner-only.
 * Each roster represents a per-game line-up with its own captain so a
 * single team can field different captains for, say, Valorant vs Apex.
 */
export function TeamHqModal({ teamId, onClose, onToast }) {
  const { user } = useAuth();
  const [hq, setHq] = useState(null);
  const [games, setGames] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editingRosterId, setEditingRosterId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("rosters");           // rosters | members | identity | recruit
  const [posts, setPosts] = useState([]);
  const [joinReqs, setJoinReqs] = useState([]);
  const [reqBusyId, setReqBusyId] = useState("");

  const load = async (opts) => {
    if (opts && opts.closed) { onClose?.(); return; }
    if (!teamId) return;
    try {
      const h = await fetchTeamHq(teamId);
      setHq(h);
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't load team.");
    }
  };

  const loadPosts = async () => {
    if (!teamId) return;
    try {
      const list = await fetchRecruitment({ team_id: teamId });
      setPosts(list);
    } catch { /* ignore */ }
  };

  const loadJoinReqs = async () => {
    if (!teamId) return;
    try { setJoinReqs(await fetchTeamJoinRequests(teamId)); }
    catch { setJoinReqs([]); }
  };

  const decideJoinReq = async (msgId, action) => {
    setReqBusyId(msgId);
    try {
      await decideInbox(msgId, action);
      onToast?.(action === "accept" ? "✅ Player added." : "Declined.");
      await Promise.all([load(), loadJoinReqs()]);
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't update request.");
    } finally { setReqBusyId(""); }
  };

  useEffect(() => {
    if (!teamId) return;
    load();
    loadPosts();
    loadJoinReqs();
    fetchGames().then(setGames).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  if (!teamId || !hq) return null;

  return (
    <div
      className="fixed inset-0 z-[95] bg-black/85 backdrop-blur-md p-2 sm:p-4 flex items-center justify-center"
      onClick={onClose}
      data-testid="team-hq-backdrop"
    >
      <div
        className="glass neon-glow-pink rounded-3xl p-4 sm:p-6 w-full max-w-3xl max-h-[94vh] max-h-[94dvh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="team-hq-modal"
      >
        <TeamHqInner
          hq={hq} games={games} userId={user?.user_id} user={user}
          tab={tab} setTab={setTab}
          posts={posts} reloadPosts={loadPosts}
          joinReqs={joinReqs} reqBusyId={reqBusyId} onDecideReq={decideJoinReq}
          creating={creating} setCreating={setCreating}
          editingRosterId={editingRosterId} setEditingRosterId={setEditingRosterId}
          busy={busy} setBusy={setBusy}
          reload={load} onClose={onClose} onToast={onToast}
        />
      </div>
    </div>
  );
}

function TeamHqInner({
  hq, games, userId, user, tab, setTab, posts, reloadPosts,
  joinReqs, reqBusyId, onDecideReq,
  creating, setCreating, editingRosterId, setEditingRosterId,
  busy, setBusy, reload, onClose, onToast,
}) {
  const isOwner = !!userId && userId === hq.captain_user_id;
  const isCaptain = !!userId && (hq.captains || []).includes(userId);
  const isAdmin = isOwner || isCaptain;
  const rosters = hq.rosters || [];
  // Local UI state for the "Schedule session" modal — scoped here so
  // the button and modal share the toggle without prop-drilling.
  const [scheduleOpen, setScheduleOpen] = useState(false);

  const roleFor = (uid) => (
    uid === hq.captain_user_id ? "owner" :
    (hq.captains || []).includes(uid) ? "captain" :
    (hq.member_user_ids || []).includes(uid) ? "member" : "guest"
  );

  const del = async (rosterId) => {
    if (!isAdmin) return;
    if (!window.confirm("Delete this roster?")) return;
    setBusy(true);
    try {
      await deleteRoster(hq.team_id, rosterId);
      onToast?.("🗑️ Roster deleted.");
      await reload();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't delete.");
    } finally { setBusy(false); }
  };

  const promote = async (uid, name) => {
    setBusy(true);
    try {
      await promoteCaptain(hq.team_id, uid);
      onToast?.(`👑 ${name} promoted to Captain.`);
      await reload();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't promote.");
    } finally { setBusy(false); }
  };

  const demote = async (uid, name) => {
    if (!window.confirm(`Demote ${name} back to Member?`)) return;
    setBusy(true);
    try {
      await demoteCaptain(hq.team_id, uid);
      onToast?.(`Demoted ${name}.`);
      await reload();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't demote.");
    } finally { setBusy(false); }
  };

  const kick = async (uid, name) => {
    if (!window.confirm(`Kick ${name} from the team?`)) return;
    setBusy(true);
    try {
      await kickMember(hq.team_id, uid);
      onToast?.(`Kicked ${name}.`);
      await reload();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't kick.");
    } finally { setBusy(false); }
  };

  const recruitColor =
    hq.recruitment_status === "open" ? "text-[#14b8a6] border-[#14b8a6]/40 bg-[#14b8a6]/10" :
    hq.recruitment_status === "scouting" ? "text-[#14b8a6] border-[#14b8a6]/40 bg-[#14b8a6]/10" :
    hq.recruitment_status === "closed" ? "text-white/40 border-white/15 bg-black/40" :
    "text-white/40 border-white/15 bg-black/40";

  return (
    <>
      {/* Banner */}
      {hq.banner_url && (
        <div
          className="h-24 -mx-6 -mt-6 mb-4 bg-gradient-to-br from-[#14b8a6]/40 to-[#14b8a6]/20"
          style={{ backgroundImage: `url(${hq.banner_url})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
      )}
      <div className="flex items-center justify-between mb-3 gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <AvatarChip avatarId={hq.avatar_id} pfpUrl={hq.pfp_url} size={56} />
          <div className="min-w-0">
            <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
              <i className="fas fa-shield-halved mr-1" />TEAM HQ
            </p>
            <h2 className="font-display text-2xl grad-text truncate" data-testid="team-hq-name">{hq.name}</h2>
            <p className="text-[11px] text-[#a3a3ac] truncate">
              {hq.league?.name} · {hq.region || "—"} · {hq.competitive_level || "casual"} · MMR {hq.mmr || 0}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hq.recruitment_status && (
            <span className={`text-[9px] tracking-widest font-bold px-2 py-0.5 rounded-md border ${recruitColor}`}
                  data-testid="team-hq-recruit-chip">
              {hq.recruitment_status.toUpperCase()}
            </span>
          )}
          <button onClick={onClose} data-testid="team-hq-close" className="text-white/60 hover:text-white text-2xl">
            <i className="fas fa-times" />
          </button>
        </div>
      </div>

      {/* Description (if any) */}
      {hq.description && (
        <p className="text-[12px] text-white/70 mb-3 leading-snug" data-testid="team-hq-description">
          {hq.description}
        </p>
      )}

      {/* Captain-only quick action — schedule a scrim / league play session.
          Opens SessionScheduleModal which POSTs to /api/calendar/sessions
          and fans out invites via the inbox. */}
      {isAdmin && (
        <div className="mb-3">
          <button
            type="button"
            onClick={() => setScheduleOpen(true)}
            data-testid="team-hq-schedule-session"
            className="text-[10px] tracking-widest px-3 py-2 rounded-xl bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40 hover:bg-[#14b8a6]/25"
          >
            <i className="fas fa-calendar-plus mr-1" />SCHEDULE SESSION
          </button>
        </div>
      )}
      <SessionScheduleModal
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        team={{
          team_id: hq.team_id,
          name: hq.name,
          captain_user_id: hq.captain_user_id,
          game: hq.game,
        }}
        members={hq.members || []}
        league={hq.league}
        onToast={onToast}
      />

      {/* Pending join requests — surfaced on the team site for owners */}
      {isOwner && (joinReqs || []).length > 0 && (
        <div className="mb-4 glass rounded-2xl p-3 border border-[#14b8a6]/40" data-testid="team-hq-pending-requests">
          <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold mb-2">
            <i className="fas fa-user-plus mr-1" />PENDING JOIN REQUESTS · {joinReqs.length}
          </p>
          <div className="space-y-1.5">
            {joinReqs.map((r) => {
              const p = r.payload || {};
              return (
                <div
                  key={r.message_id}
                  data-testid={`team-hq-req-${r.message_id}`}
                  className="flex items-center gap-2 p-2 rounded-xl bg-black/30 border border-white/10"
                >
                  <AvatarChip avatarId={p.requester_avatar_id} pfpUrl={p.requester_pfp_url} size={32} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] font-bold text-white truncate">{p.requester_name}</p>
                    <p className="text-[10px] text-[#a3a3ac] truncate">
                      wants to join · MMR {p.requester_elo || "—"}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onDecideReq(r.message_id, "accept")}
                    disabled={reqBusyId === r.message_id}
                    data-testid={`team-hq-req-accept-${r.message_id}`}
                    title="Approve"
                    className="w-7 h-7 rounded-md bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/50 hover:bg-[#14b8a6]/25 disabled:opacity-50 flex items-center justify-center"
                  >
                    <i className="fas fa-check text-[10px]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => onDecideReq(r.message_id, "decline")}
                    disabled={reqBusyId === r.message_id}
                    data-testid={`team-hq-req-decline-${r.message_id}`}
                    title="Decline"
                    className="w-7 h-7 rounded-md bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40 hover:bg-[#14b8a6]/25 disabled:opacity-50 flex items-center justify-center"
                  >
                    <i className="fas fa-xmark text-[10px]" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-4 flex-wrap" data-testid="team-hq-tabs">
        {[
          { id: "rosters", label: `ROSTERS · ${rosters.length}` },
          { id: "members", label: `TEAM · ${(hq.members || []).length}` },
          { id: "availability", label: "AVAILABILITY" },
          { id: "chat", label: "CHAT" },
          { id: "tryouts", label: "TRYOUTS" },
          ...(isAdmin ? [{ id: "recruit", label: "RECRUIT" }] : []),
          ...(isOwner ? [{ id: "identity", label: "IDENTITY" }] : []),
          ...(isOwner ? [{ id: "manage", label: "MANAGE", danger: true }] : []),
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            data-testid={`team-hq-tab-${t.id}`}
            className={`text-[10px] tracking-widest px-3 py-1.5 rounded-xl transition-colors ${
              tab === t.id
                ? (t.danger
                    ? "bg-[#14b8a6]/20 text-[#14b8a6] border border-[#14b8a6]/50"
                    : "bg-[#14b8a6]/20 text-[#14b8a6] border border-[#14b8a6]/40")
                : (t.danger
                    ? "bg-black/30 text-[#14b8a6]/70 border border-[#14b8a6]/30 hover:text-[#14b8a6]"
                    : "bg-black/30 text-white/55 border border-white/10 hover:text-white")
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "members" && (
        <MembersTab
          hq={hq} userId={userId} isOwner={isOwner} roleFor={roleFor}
          onPromote={promote} onDemote={demote} onKick={kick} busy={busy}
          onToast={onToast} reload={reload}
        />
      )}

      {tab === "chat" && (
        <TeamChat teamId={hq.team_id} user={user} onToast={onToast} />
      )}

      {tab === "availability" && (
        <AvailabilityTab
          teamId={hq.team_id}
          userId={userId}
          onToast={onToast}
        />
      )}

      {tab === "tryouts" && (
        <TryoutsTab hq={hq} isAdmin={isAdmin} games={games} onToast={onToast} reloadHq={reload} />
      )}

      {tab === "identity" && isOwner && (
        <IdentityTab hq={hq} games={games} onToast={onToast} reload={reload} />
      )}

      {tab === "manage" && isOwner && (
        <ManageTab hq={hq} onToast={onToast} reload={reload} />
      )}

      {tab === "recruit" && isAdmin && (
        <RecruitTab hq={hq} posts={posts} reloadPosts={reloadPosts}
                    games={games} onToast={onToast} />
      )}

      {tab === "rosters" && (
        <RostersTab
          hq={hq} games={games} rosters={rosters}
          isAdmin={isAdmin} creating={creating} setCreating={setCreating}
          editingRosterId={editingRosterId} setEditingRosterId={setEditingRosterId}
          busy={busy} setBusy={setBusy} reload={reload} onToast={onToast}
          del={del}
        />
      )}
    </>
  );
}

function MembersTab({ hq, userId, isOwner, roleFor, onPromote, onDemote, onKick, busy, onToast, reload }) {
  // Phase 59 — pull supplemental role + status data so we can render
  // SUB / BENCHED / SUSPENDED badges next to each member. Falls back to
  // the legacy role inference when the new endpoint hasn't been hit
  // yet (e.g. mid-render before the effect resolves).
  const [extras, setExtras] = React.useState({}); // user_id -> {role, status}
  const [rosterBusy, setRosterBusy] = React.useState("");

  const loadExtras = React.useCallback(async () => {
    if (!hq?.team_id) return;
    try {
      const { listTeamRoles } = await import("../lib/api");
      const res = await listTeamRoles(hq.team_id);
      const map = {};
      (res.roles || []).forEach((r) => {
        map[r.user_id] = { role: r.role, status: r.status || "active" };
      });
      setExtras(map);
    } catch (_e) { /* roles endpoint 403 for non-members */ }
  }, [hq?.team_id]);
  React.useEffect(() => { loadExtras(); }, [loadExtras]);

  const flipSub = async (uid, name, makeSub) => {
    setRosterBusy(uid);
    try {
      const { setTeamRole, clearTeamRole } = await import("../lib/api");
      if (makeSub) {
        await setTeamRole(hq.team_id, uid, "substitute");
        onToast?.(`🎒 ${name} moved to the bench (substitute).`);
      } else {
        // Clear the substitute role → defaults back to 'player'.
        await clearTeamRole(hq.team_id, uid);
        onToast?.(`✅ ${name} returned to the starting roster.`);
      }
      await loadExtras();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't update role.");
    } finally { setRosterBusy(""); }
  };

  const setStatus = async (uid, name, status) => {
    if (status === "suspended" && !window.confirm(`Suspend ${name}? They won't be able to participate in matches.`)) return;
    setRosterBusy(uid);
    try {
      const { setTeamMemberStatus } = await import("../lib/api");
      await setTeamMemberStatus(hq.team_id, uid, status);
      onToast?.(
        status === "benched" ? `🪑 ${name} benched.` :
        status === "suspended" ? `⛔ ${name} suspended.` :
        `▶ ${name} reactivated.`
      );
      await loadExtras();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't update status.");
    } finally { setRosterBusy(""); }
  };

  return (
    <div className="space-y-1.5" data-testid="team-hq-members">
      {(hq.members || []).map((m) => {
        const role = roleFor(m.user_id);
        const ex = extras[m.user_id] || {};
        const isSub = ex.role === "substitute";
        const status = ex.status || "active";
        const isCaptainOrOwner = role === "captain" || role === "owner";
        const myBusy = rosterBusy === m.user_id || busy;
        return (
          <div
            key={m.user_id}
            data-testid={`team-hq-member-${m.user_id}`}
            className={`glass rounded-xl p-2.5 flex flex-wrap items-center gap-2 border ${
              status === "suspended" ? "border-red-500/40 opacity-70" :
              status === "benched"   ? "border-yellow-400/40" :
              "border-white/10"
            }`}
          >
            <AvatarChip avatarId={m.avatar_id} pfpUrl={m.pfp_url} size={32} />
            <div className="flex-1 min-w-[160px]">
              <p className="font-bold text-sm text-white truncate flex items-center gap-1.5">
                {m.name}
                {isSub && (
                  <span
                    data-testid={`team-hq-sub-chip-${m.user_id}`}
                    className="text-[8px] tracking-widest px-1.5 py-0.5 rounded-full bg-fuchsia-400/15 text-fuchsia-300 border border-fuchsia-400/40"
                  >SUB</span>
                )}
                {status === "benched" && (
                  <span
                    data-testid={`team-hq-benched-chip-${m.user_id}`}
                    className="text-[8px] tracking-widest px-1.5 py-0.5 rounded-full bg-yellow-400/15 text-yellow-300 border border-yellow-400/40"
                  >BENCHED</span>
                )}
                {status === "suspended" && (
                  <span
                    data-testid={`team-hq-suspended-chip-${m.user_id}`}
                    className="text-[8px] tracking-widest px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-300 border border-red-500/40"
                  >SUSPENDED</span>
                )}
              </p>
              <p className="text-[10px] text-[#a3a3ac] truncate">
                MMR {m.elo || 1500} · {role.toUpperCase()}
              </p>
            </div>
            {role === "owner" && <i className="fas fa-crown text-[#14b8a6]" title="Team Owner" />}
            {role === "captain" && <i className="fas fa-star text-[#14b8a6]" title="Team Captain" />}
            {isOwner && m.user_id !== userId && (
              <div className="flex flex-wrap gap-1">
                {role === "member" && (
                  <button
                    type="button"
                    onClick={() => onPromote(m.user_id, m.name)}
                    disabled={myBusy}
                    data-testid={`team-hq-promote-${m.user_id}`}
                    className="text-[10px] tracking-widest px-2 py-1 rounded-lg btn-ghost"
                  >
                    <i className="fas fa-arrow-up mr-1" />CO-CAP
                  </button>
                )}
                {role === "captain" && (
                  <button
                    type="button"
                    onClick={() => onDemote(m.user_id, m.name)}
                    disabled={myBusy}
                    data-testid={`team-hq-demote-${m.user_id}`}
                    className="text-[10px] tracking-widest px-2 py-1 rounded-lg btn-ghost"
                  >
                    <i className="fas fa-arrow-down mr-1" />DEMOTE
                  </button>
                )}
                {/* Substitute toggle — never apply to captains. */}
                {!isCaptainOrOwner && (
                  <button
                    type="button"
                    onClick={() => flipSub(m.user_id, m.name, !isSub)}
                    disabled={myBusy}
                    data-testid={`team-hq-sub-toggle-${m.user_id}`}
                    className={`text-[10px] tracking-widest px-2 py-1 rounded-lg ${
                      isSub
                        ? "bg-fuchsia-400/20 text-fuchsia-300 border border-fuchsia-400/45"
                        : "btn-ghost"
                    }`}
                    title={isSub ? "Return to starting roster" : "Move to bench"}
                  >
                    <i className="fas fa-people-arrows mr-1" />{isSub ? "STARTER" : "SUB"}
                  </button>
                )}
                {/* Bench toggle */}
                {!isCaptainOrOwner && status !== "suspended" && (
                  <button
                    type="button"
                    onClick={() => setStatus(m.user_id, m.name, status === "benched" ? "active" : "benched")}
                    disabled={myBusy}
                    data-testid={`team-hq-bench-${m.user_id}`}
                    className={`text-[10px] tracking-widest px-2 py-1 rounded-lg ${
                      status === "benched"
                        ? "bg-yellow-400/20 text-yellow-300 border border-yellow-400/45"
                        : "btn-ghost"
                    }`}
                  >
                    <i className="fas fa-couch mr-1" />{status === "benched" ? "ACTIVATE" : "BENCH"}
                  </button>
                )}
                {/* Suspend / unsuspend — captain power only. */}
                {!isCaptainOrOwner && (
                  <button
                    type="button"
                    onClick={() => setStatus(m.user_id, m.name, status === "suspended" ? "active" : "suspended")}
                    disabled={myBusy}
                    data-testid={`team-hq-suspend-${m.user_id}`}
                    className={`text-[10px] tracking-widest px-2 py-1 rounded-lg ${
                      status === "suspended"
                        ? "bg-red-500/20 text-red-300 border border-red-500/45"
                        : "btn-ghost"
                    }`}
                    title={status === "suspended" ? "Lift suspension" : "Suspend"}
                  >
                    <i className="fas fa-ban mr-1" />{status === "suspended" ? "UNSUSPEND" : "SUSPEND"}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onKick(m.user_id, m.name)}
                  disabled={myBusy}
                  data-testid={`team-hq-kick-${m.user_id}`}
                  className="text-[10px] tracking-widest px-2 py-1 rounded-lg bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40"
                  title="Kick from team"
                >
                  <i className="fas fa-user-slash" />
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function IdentityTab({ hq, games, onToast, reload }) {
  const [form, setForm] = useState({
    description: hq.description || "",
    region: hq.region || "",
    competitive_level: hq.competitive_level || "casual",
    recruitment_status: hq.recruitment_status || "closed",
    is_private: !!hq.is_private,
    supported_games: hq.supported_games || [],
    banner_url: hq.banner_url || "",
  });
  const [busy, setBusy] = useState(false);

  const onBannerFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 3_000_000) { onToast?.("Banner too large (max 3 MB)."); return; }
    const r = new FileReader();
    r.onload = () => setForm((p) => ({ ...p, banner_url: r.result }));
    r.readAsDataURL(f);
  };

  const toggleGame = (g) => setForm((p) => ({
    ...p,
    supported_games: p.supported_games.includes(g)
      ? p.supported_games.filter((x) => x !== g)
      : [...p.supported_games, g],
  }));

  const save = async () => {
    setBusy(true);
    try {
      await updateTeamHq(hq.team_id, form);
      onToast?.("✅ Team updated.");
      await reload();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't save.");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="team-hq-identity">
      <div>
        <label className="block text-[10px] tracking-widest text-[#14b8a6] mb-1">DESCRIPTION</label>
        <textarea
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          maxLength={800}
          rows={3}
          data-testid="team-hq-edit-description"
          className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white text-sm"
        />
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-[10px] tracking-widest text-[#a3a3ac] mb-1">REGION</label>
          <input
            value={form.region}
            onChange={(e) => setForm({ ...form, region: e.target.value })}
            placeholder="NA, EU, APAC…"
            data-testid="team-hq-edit-region"
            className="w-full bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-white text-sm"
          />
        </div>
        <div>
          <label className="block text-[10px] tracking-widest text-[#14b8a6] mb-1">LEVEL</label>
          <select
            value={form.competitive_level}
            onChange={(e) => setForm({ ...form, competitive_level: e.target.value })}
            data-testid="team-hq-edit-level"
            className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white text-sm"
          >
            <option value="casual">Casual</option>
            <option value="competitive">Competitive</option>
            <option value="pro">Pro</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] tracking-widest text-[#14b8a6] mb-1">RECRUITMENT</label>
          <select
            value={form.recruitment_status}
            onChange={(e) => setForm({ ...form, recruitment_status: e.target.value })}
            data-testid="team-hq-edit-recruit"
            className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white text-sm"
          >
            <option value="closed">Closed</option>
            <option value="scouting">Scouting</option>
            <option value="open">Open</option>
          </select>
        </div>
      </div>
      <div>
        <label className="block text-[10px] tracking-widest text-[#a3a3ac] mb-1">SUPPORTED GAMES</label>
        <div className="flex flex-wrap gap-1.5">
          {games.map((g) => {
            const on = form.supported_games.includes(g.name);
            return (
              <button
                key={g.slug}
                type="button"
                onClick={() => toggleGame(g.name)}
                data-testid={`team-hq-edit-game-${g.slug}`}
                className={`text-[10px] tracking-wide px-2 py-1 rounded-lg border transition ${
                  on
                    ? "bg-[#14b8a6]/15 border-[#14b8a6] text-[#14b8a6]"
                    : "bg-black/40 border-white/15 text-white/55 hover:border-[#14b8a6]/40"
                }`}
              >
                {on && <i className="fas fa-check text-[9px] mr-1" />}{g.name}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <label className="block text-[10px] tracking-widest text-[#a3a3ac] mb-1">BANNER</label>
        <input type="file" accept="image/*" onChange={onBannerFile} data-testid="team-hq-edit-banner"
               className="text-[11px] text-white/65 file:bg-black/40 file:text-white file:border-0 file:rounded-md file:px-3 file:py-1.5 file:mr-2" />
        {form.banner_url && (
          <div className="mt-2 h-16 rounded-xl border border-white/10"
               style={{ backgroundImage: `url(${form.banner_url})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        )}
      </div>
      <div className="flex items-center gap-2">
        <input
          id="hq-private" type="checkbox" checked={form.is_private}
          onChange={(e) => setForm({ ...form, is_private: e.target.checked })}
          data-testid="team-hq-edit-private"
        />
        <label htmlFor="hq-private" className="text-[11px] text-white/65">
          Private team (hidden from the public Teams directory).
        </label>
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={busy}
          data-testid="team-hq-edit-save"
          className="text-[11px] tracking-widest px-5 py-2 rounded-xl btn-neon"
        >
          {busy ? "..." : "SAVE IDENTITY"}
        </button>
      </div>

      {/* Disband moved to its own MANAGE tab so the danger action lives
          beside other team-management primitives, separate from cosmetic
          identity edits. */}
    </div>
  );
}

function ManageTab({ hq, onToast, reload }) {
  const isStandalone = !hq.league_id;
  return (
    <div className="space-y-3" data-testid="team-hq-manage">
      <div className="rounded-2xl border border-white/10 bg-black/30 p-4">
        <p className="text-[10px] tracking-[3px] text-[#a3a3ac] font-bold">
          <i className="fas fa-shield-halved mr-1" />TEAM MANAGEMENT
        </p>
        <p className="text-[12px] text-white/70 mt-1 leading-snug">
          Owner-only controls for <b>{hq.name}</b>. Roster captains and
          identity edits live in their own tabs; the destructive disband
          action is below.
        </p>
        <p className="text-[10px] tracking-widest text-white/45 mt-3">
          STATUS · <span className="text-[#14b8a6]">
            {isStandalone ? "STANDALONE TEAM" : "LEAGUE-ATTACHED"}
          </span>
        </p>
      </div>
      {!isStandalone && (
        <DetachFromLeagueCard hq={hq} onToast={onToast} reload={reload} />
      )}
      <DisbandHqDanger hq={hq} onToast={onToast} reload={reload} />
    </div>
  );
}

function DetachFromLeagueCard({ hq, onToast, reload }) {
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const detach = async () => {
    setBusy(true);
    try {
      const { detachTeamFromLeague } = await import("../lib/api");
      const res = await detachTeamFromLeague(hq.team_id);
      onToast?.(
        res.already_standalone
          ? "Already standalone."
          : `${hq.name} is now a standalone team.`
      );
      await reload?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't detach.");
    } finally { setBusy(false); }
  };
  return (
    <div
      data-testid="team-hq-detach-zone"
      className="rounded-2xl border border-[rgba(20,184,166,0.25)] bg-black/30 p-4"
    >
      <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
        <i className="fas fa-link-slash mr-1" />DETACH FROM LEAGUE
      </p>
      {!confirm ? (
        <div className="flex items-center justify-between gap-3 mt-2">
          <p className="text-[12px] text-white/75 flex-1 leading-snug">
            Pop this team out of its parent league so it runs as a
            <b className="text-[#14b8a6]"> standalone Team Hub</b>. You'll
            free your league slot, pending challenges in this league
            cancel, and you can run per-game rosters without a league.
            Match history is preserved.
          </p>
          <button
            type="button"
            onClick={() => setConfirm(true)}
            data-testid="team-hq-detach-btn"
            className="text-[11px] tracking-widest px-4 py-2 rounded-xl bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/50 hover:bg-[#14b8a6]/25"
          >
            DETACH
          </button>
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          <p className="text-[12px] text-white/85">
            Make <code className="text-[#14b8a6] font-mono">{hq.name}</code> a
            standalone team? You can never re-attach it to the same league
            without rejoining.
          </p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setConfirm(false)}
              disabled={busy}
              data-testid="team-hq-detach-cancel"
              className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-ghost"
            >
              CANCEL
            </button>
            <button
              type="button"
              onClick={detach}
              disabled={busy}
              data-testid="team-hq-detach-confirm"
              className="text-[11px] tracking-widest px-5 py-2 rounded-xl bg-[#14b8a6] text-black font-bold disabled:opacity-60"
            >
              {busy ? "DETACHING…" : "CONFIRM DETACH"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function DisbandHqDanger({ hq, onToast, reload }) {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const onConfirm = async () => {
    if (confirm.trim().toLowerCase() !== (hq.name || "").trim().toLowerCase()) {
      onToast?.(`Type the team name exactly: ${hq.name}`);
      return;
    }
    setBusy(true);
    try {
      const { disbandTeam } = await import("../lib/api");
      const res = await disbandTeam(hq.team_id);
      onToast?.(
        `${res.team_name || "TEAM"} disbanded. ${res.challenges_cancelled || 0} matches cancelled.`
      );
      // Bubble up — the parent modal closes itself on null reload.
      await reload?.({ closed: true });
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't disband.");
    } finally { setBusy(false); }
  };
  return (
    <div
      data-testid="team-hq-disband-zone"
      className="mt-4 rounded-2xl border-2 border-[#14b8a6]/50 bg-[#14b8a6]/5 p-4"
    >
      <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
        <i className="fas fa-triangle-exclamation mr-1" />DANGER ZONE
      </p>
      {!open ? (
        <div className="flex items-center justify-between gap-3 mt-2">
          <p className="text-[12px] text-white/75 flex-1">
            Permanently disband <b>{hq.name}</b>. Wipes rosters, chat, tryouts,
            recruitment posts &amp; pending invites. Completed match history
            stays for leaderboards. <b>This can't be undone.</b>
          </p>
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid="team-hq-disband-btn"
            className="text-[11px] tracking-widest px-4 py-2 rounded-xl bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/50 hover:bg-[#14b8a6]/25"
          >
            DISBAND TEAM
          </button>
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          <p className="text-[12px] text-white/85">
            Type <code className="text-[#14b8a6] font-mono">{hq.name}</code> to confirm.
          </p>
          <input
            type="text"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder={hq.name}
            data-testid="team-hq-disband-confirm-input"
            className="w-full bg-black/50 border border-[#14b8a6]/50 rounded-xl px-3 py-2 text-white"
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => { setOpen(false); setConfirm(""); }}
              disabled={busy}
              data-testid="team-hq-disband-cancel"
              className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-ghost"
            >
              CANCEL
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy}
              data-testid="team-hq-disband-confirm"
              className="text-[11px] tracking-widest px-5 py-2 rounded-xl bg-[#14b8a6] text-black font-bold disabled:opacity-60"
            >
              {busy ? "DISBANDING…" : "DISBAND FOREVER"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function RecruitTab({ hq, posts, reloadPosts, games, onToast }) {
  const [form, setForm] = useState({ game: games[0]?.name || "", role: "", rank: "", description: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!form.game && games[0]) setForm((p) => ({ ...p, game: games[0].name }));
    // eslint-disable-next-line
  }, [games.length]);

  const submit = async () => {
    if (!form.game.trim()) { onToast?.("Pick a game."); return; }
    setBusy(true);
    try {
      await createRecruitment({ team_id: hq.team_id, ...form });
      onToast?.("📣 Recruitment post live.");
      setForm({ game: form.game, role: "", rank: "", description: "" });
      await reloadPosts();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't post.");
    } finally { setBusy(false); }
  };

  const close = async (postId) => {
    setBusy(true);
    try {
      await closeRecruitment(postId);
      onToast?.("Post closed.");
      await reloadPosts();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't close.");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="team-hq-recruit">
      <div className="glass rounded-2xl p-3 border border-[#14b8a6]/40 space-y-3">
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">NEW POST</p>
        <div className="grid sm:grid-cols-3 gap-2">
          <select value={form.game} onChange={(e) => setForm({ ...form, game: e.target.value })}
                  data-testid="team-hq-recruit-game"
                  className="bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white text-sm">
            {games.map((g) => <option key={g.slug} value={g.name}>{g.name}</option>)}
          </select>
          <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                 placeholder="Role (IGL, Sub, Duelist…)"
                 data-testid="team-hq-recruit-role"
                 className="bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-white text-sm" />
          <input value={form.rank} onChange={(e) => setForm({ ...form, rank: e.target.value })}
                 placeholder="Rank (Diamond+, etc.)"
                 data-testid="team-hq-recruit-rank"
                 className="bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-white text-sm" />
        </div>
        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2} placeholder="What you're looking for, vibes, tryout details…"
                  data-testid="team-hq-recruit-desc"
                  className="w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm" />
        <div className="flex justify-end">
          <button type="button" onClick={submit} disabled={busy}
                  data-testid="team-hq-recruit-submit"
                  className="text-[11px] tracking-widest px-4 py-1.5 rounded-xl btn-neon">
            {busy ? "..." : "POST"}
          </button>
        </div>
      </div>
      <div className="space-y-2">
        {posts.length === 0
          ? <p className="text-[11px] text-white/45" data-testid="team-hq-recruit-empty">No posts yet — make one above.</p>
          : posts.map((p) => (
            <div key={p.post_id} data-testid={`team-hq-recruit-${p.post_id}`}
                 className="glass rounded-2xl p-3 border border-[#14b8a6]/30 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] tracking-widest text-[#14b8a6] font-bold">{p.role || "PLAYER WANTED"} · {p.game}</p>
                <p className="text-[12px] text-white/75 mt-0.5 line-clamp-2">{p.description}</p>
                {p.rank && <p className="text-[10px] text-[#a3a3ac] mt-1">Rank: {p.rank}</p>}
              </div>
              <button type="button" onClick={() => close(p.post_id)} disabled={busy}
                      data-testid={`team-hq-recruit-close-${p.post_id}`}
                      className="text-[10px] tracking-widest px-2.5 py-1 rounded-lg bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40">
                CLOSE
              </button>
            </div>
          ))}
      </div>
    </div>
  );
}

function RostersTab({
  hq, games, rosters, isAdmin, creating, setCreating,
  editingRosterId, setEditingRosterId, busy, setBusy, reload, onToast, del,
}) {
  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
          <i className="fas fa-list-ol mr-1" />ROSTERS · {rosters.length}
        </p>
        {isAdmin && !creating && (
          <button
            type="button"
            onClick={() => { setCreating(true); setEditingRosterId(null); }}
            data-testid="team-hq-add-roster-btn"
            className="text-[10px] tracking-widest px-3 py-1.5 rounded-xl btn-neon"
          >
            <i className="fas fa-plus mr-1" />NEW ROSTER
          </button>
        )}
      </div>

      {creating && (
        <RosterEditor
          team={hq} games={games} initial={null}
          onSave={async (body) => {
            setBusy(true);
            try {
              await createRoster(hq.team_id, body);
              onToast?.("✅ Roster created.");
              setCreating(false);
              await reload();
            } catch (e) {
              onToast?.(e?.response?.data?.detail || "Couldn't create roster.");
            } finally { setBusy(false); }
          }}
          onCancel={() => setCreating(false)}
          busy={busy}
        />
      )}

      {rosters.length === 0 && !creating ? (
        <p className="text-xs text-white/45 py-6 text-center" data-testid="team-hq-empty">
          {isAdmin
            ? "No rosters yet. Hit + NEW ROSTER above to build one per game."
            : "The team owner hasn't created any rosters yet."}
        </p>
      ) : (
        <div className="space-y-3">
          {rosters.map((r) => {
            const editing = editingRosterId === r.roster_id;
            if (editing) {
              return (
                <RosterEditor
                  key={r.roster_id}
                  team={hq} games={games} initial={r}
                  onSave={async (body) => {
                    setBusy(true);
                    try {
                      await updateRoster(hq.team_id, r.roster_id, body);
                      onToast?.("✅ Roster updated.");
                      setEditingRosterId(null);
                      await reload();
                    } catch (e) {
                      onToast?.(e?.response?.data?.detail || "Couldn't update roster.");
                    } finally { setBusy(false); }
                  }}
                  onCancel={() => setEditingRosterId(null)}
                  busy={busy}
                />
              );
            }
            return <RosterCard key={r.roster_id} team={hq} r={r} isAdmin={isAdmin}
              onEdit={() => { setEditingRosterId(r.roster_id); setCreating(false); }}
              onDelete={() => del(r.roster_id)}
              onToggleLock={async () => {
                setBusy(true);
                try {
                  await updateRoster(hq.team_id, r.roster_id, { locked: !r.locked });
                  onToast?.(r.locked ? "🔓 Roster unlocked." : "🔒 Roster locked.");
                  await reload();
                } catch (e) {
                  onToast?.(e?.response?.data?.detail || "Couldn't toggle.");
                } finally { setBusy(false); }
              }}
              busy={busy} />;
          })}
        </div>
      )}
    </>
  );
}

function RosterCard({ team, r, isAdmin, onEdit, onDelete, onToggleLock, busy }) {
  const byId = Object.fromEntries((team.members || []).map((m) => [m.user_id, m]));
  const starters = (r.starter_user_ids || []).map((u) => byId[u]).filter(Boolean);
  const subs = (r.sub_user_ids || []).map((u) => byId[u]).filter(Boolean);
  const coaches = (r.coach_user_ids || []).map((u) => byId[u]).filter(Boolean);
  const fallbackMembers = !starters.length && !subs.length
    ? (r.members || []).filter(Boolean) : [];
  return (
    <div
      data-testid={`team-hq-roster-${r.roster_id}`}
      className={`glass rounded-2xl p-3 border ${r.locked ? "border-[#14b8a6]/40 bg-[#14b8a6]/5" : "border-[#14b8a6]/30"}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[9px] tracking-widest text-[#14b8a6] font-bold">
            <i className="fas fa-gamepad mr-1" />{r.game}
            {r.locked && <span className="ml-2 text-[#14b8a6]"><i className="fas fa-lock mr-1" />LOCKED</span>}
          </p>
          <p className="font-bold text-sm truncate">{r.name || `${r.game} roster`}</p>
        </div>
        {isAdmin && (
          <div className="flex gap-1.5">
            <button type="button" onClick={onToggleLock} disabled={busy}
                    data-testid={`team-hq-roster-lock-${r.roster_id}`}
                    className={`text-[10px] tracking-widest px-2.5 py-1 rounded-lg ${r.locked ? "bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40" : "btn-ghost"}`}>
              {r.locked ? <><i className="fas fa-unlock mr-1" />UNLOCK</> : <><i className="fas fa-lock mr-1" />LOCK</>}
            </button>
            <button type="button" onClick={onEdit} data-testid={`team-hq-roster-edit-${r.roster_id}`}
                    className="text-[10px] tracking-widest px-2.5 py-1 rounded-lg btn-ghost">
              <i className="fas fa-pen mr-1" />EDIT
            </button>
            <button type="button" onClick={onDelete} disabled={busy}
                    data-testid={`team-hq-roster-delete-${r.roster_id}`}
                    className="text-[10px] tracking-widest px-2.5 py-1 rounded-lg bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40">
              <i className="fas fa-trash" />
            </button>
          </div>
        )}
      </div>
      {(starters.length || subs.length || coaches.length || fallbackMembers.length) ? (
        <div className="mt-2 space-y-2 text-[11px]">
          {(starters.length > 0 || fallbackMembers.length > 0) && (
            <PlayerStrip label="STARTERS" color="text-[#14b8a6]"
                         players={starters.length ? starters : fallbackMembers}
                         captainId={r.captain_user_id}
                         testidBase={`team-hq-roster-${r.roster_id}-starter`} />
          )}
          {subs.length > 0 && (
            <PlayerStrip label="SUBS" color="text-[#a3a3ac]" players={subs}
                         testidBase={`team-hq-roster-${r.roster_id}-sub`} />
          )}
          {coaches.length > 0 && (
            <PlayerStrip label="COACH" color="text-[#14b8a6]" players={coaches}
                         testidBase={`team-hq-roster-${r.roster_id}-coach`} />
          )}
        </div>
      ) : null}
    </div>
  );
}

function PlayerStrip({ label, color, players, captainId, testidBase }) {
  return (
    <div>
      <p className={`text-[9px] tracking-widest font-bold mb-1 ${color}`}>{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {players.map((m) => (
          <span key={m.user_id} data-testid={`${testidBase}-${m.user_id}`}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-black/40 border ${
                  m.user_id === captainId ? "border-[#14b8a6]/60 text-[#14b8a6]" : "border-white/15 text-white/85"
                }`}>
            <AvatarChip avatarId={m.avatar_id} pfpUrl={m.pfp_url} size={18} />
            <span className="text-[11px] font-bold truncate max-w-[110px]">{m.name}</span>
            {m.user_id === captainId && <i className="fas fa-crown text-[9px]" title="Roster captain" />}
          </span>
        ))}
      </div>
    </div>
  );
}


function RosterEditor({ team, games, initial, onSave, onCancel, busy }) {
  const [name, setName] = useState(initial?.name || "");
  const [game, setGame] = useState(initial?.game || (games[0]?.name || ""));
  const [captainId, setCaptainId] = useState(initial?.captain_user_id || team.captain_user_id || "");
  const [starters, setStarters] = useState(initial?.starter_user_ids || []);
  const [subs, setSubs] = useState(initial?.sub_user_ids || []);
  const [coaches, setCoaches] = useState(initial?.coach_user_ids || []);

  // Slot rotation: starter → sub → coach → off → starter
  const cycleSlot = (uid) => {
    const isStarter = starters.includes(uid);
    const isSub = subs.includes(uid);
    const isCoach = coaches.includes(uid);
    const clear = (arr) => arr.filter((x) => x !== uid);
    if (!isStarter && !isSub && !isCoach) { setStarters([...starters, uid]); return; }
    if (isStarter) { setStarters(clear(starters)); setSubs([...subs, uid]); return; }
    if (isSub) { setSubs(clear(subs)); setCoaches([...coaches, uid]); return; }
    if (isCoach) { setCoaches(clear(coaches)); return; }
  };

  const slotOf = (uid) => (
    starters.includes(uid) ? "starter" :
    subs.includes(uid) ? "sub" :
    coaches.includes(uid) ? "coach" : "off"
  );

  const submit = () => {
    if (!game.trim()) return;
    // members = union of all slot lists + captain if set
    const memberIds = Array.from(new Set([
      ...(captainId ? [captainId] : []),
      ...starters, ...subs, ...coaches,
    ]));
    onSave({
      name: name.trim(),
      game: game.trim(),
      captain_user_id: captainId || null,
      member_user_ids: memberIds,
      starter_user_ids: starters,
      sub_user_ids: subs,
      coach_user_ids: coaches,
    });
  };

  const slotColor = {
    starter: "bg-[#14b8a6]/15 border-[#14b8a6] text-[#14b8a6]",
    sub: "bg-[#14b8a6]/15 border-[#14b8a6] text-[#14b8a6]",
    coach: "bg-[#14b8a6]/15 border-[#14b8a6] text-[#14b8a6]",
    off: "bg-black/40 border-white/15 text-white/55 hover:border-[#14b8a6]/40",
  };
  const slotLetter = { starter: "S", sub: "B", coach: "C", off: "" };

  return (
    <div
      data-testid="team-hq-roster-editor"
      className="glass rounded-2xl p-4 border border-[#14b8a6]/40 space-y-3 mb-3"
    >
      <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
        {initial ? "EDIT ROSTER" : "NEW ROSTER"}
      </p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-[10px] tracking-widest text-[#14b8a6] mb-1">NAME (OPTIONAL)</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={60}
            placeholder="e.g. 5-stack Ranked"
            data-testid="team-hq-roster-name"
            className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white text-sm"
          />
        </div>
        <div>
          <label className="block text-[10px] tracking-widest text-[#14b8a6] mb-1">GAME</label>
          <select
            value={game}
            onChange={(e) => setGame(e.target.value)}
            data-testid="team-hq-roster-game"
            className="w-full bg-black/40 border border-[#14b8a6]/30 rounded-xl px-3 py-2 text-white text-sm"
          >
            {games.map((g) => (
              <option key={g.slug} value={g.name}>{g.name}</option>
            ))}
            {games.length === 0 && initial?.game && <option value={initial.game}>{initial.game}</option>}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-[10px] tracking-widest text-[#a3a3ac] mb-1">CAPTAIN (PER-GAME)</label>
        <select
          value={captainId}
          onChange={(e) => setCaptainId(e.target.value)}
          data-testid="team-hq-roster-captain"
          className="w-full bg-black/40 border border-white/20 rounded-xl px-3 py-2 text-white text-sm"
        >
          <option value="">— No specific captain —</option>
          {(team.members || []).map((m) => (
            <option key={m.user_id} value={m.user_id}>{m.name}{m.user_id === team.captain_user_id ? " (team owner)" : ""}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-[10px] tracking-widest text-[#a3a3ac] mb-1">
          SLOTS <span className="text-white/40">— tap to cycle Starter ▸ Sub ▸ Coach ▸ off</span>
        </label>
        <div className="flex flex-wrap gap-1.5">
          {(team.members || []).map((m) => {
            const slot = slotOf(m.user_id);
            return (
              <button
                key={m.user_id}
                type="button"
                onClick={() => cycleSlot(m.user_id)}
                data-testid={`team-hq-roster-toggle-${m.user_id}`}
                title={slot.toUpperCase()}
                className={`inline-flex items-center gap-1.5 text-[11px] tracking-wide px-2 py-1 rounded-lg border transition ${slotColor[slot]}`}
              >
                <AvatarChip avatarId={m.avatar_id} pfpUrl={m.pfp_url} size={18} />
                {slotLetter[slot] && (
                  <span className="text-[9px] tracking-widest font-extrabold">{slotLetter[slot]}</span>
                )}
                {m.name}
              </button>
            );
          })}
        </div>
        <p className="text-[10px] text-white/40 mt-1">
          {starters.length} Starter{starters.length === 1 ? "" : "s"} · {subs.length} Sub{subs.length === 1 ? "" : "s"} · {coaches.length} Coach{coaches.length === 1 ? "" : "es"}
        </p>
      </div>
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          data-testid="team-hq-roster-cancel"
          className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-ghost"
        >
          CANCEL
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={busy || !game.trim()}
          data-testid="team-hq-roster-save"
          className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-neon disabled:opacity-60"
        >
          {busy ? "..." : "SAVE ROSTER"}
        </button>
      </div>
    </div>
  );
}

export default TeamHqModal;


// =============================================================================
// AvailabilityTab — captain-facing roster matrix + "my status" editor
// =============================================================================

const STATUS_CHIP = {
  available:   { color: "#14b8a6", border: "#14b8a6", label: "AVAILABLE",   icon: "fa-circle-check" },
  limited:     { color: "#a3a3ac", border: "#a3a3ac", label: "LIMITED",     icon: "fa-clock" },
  unavailable: { color: "#8a98ab", border: "#8a98ab", label: "UNAVAILABLE", icon: "fa-circle-xmark" },
  unset:       { color: "#5a6678", border: "#3a4253", label: "—",           icon: "fa-minus" },
};

function AvailabilityTab({ teamId, userId, onToast }) {
  const [data, setData] = useState(null);
  const [savingMine, setSavingMine] = useState(false);

  // Local editor state for the current user's status.
  const [pickStatus, setPickStatus] = useState("");
  const [pickUntil, setPickUntil] = useState("");
  const [pickNote, setPickNote] = useState("");

  const reload = async () => {
    try {
      const fresh = await fetchTeamAvailability(teamId);
      setData(fresh);
      const mine = (fresh.rows || []).find((r) => r.user_id === userId);
      if (mine) {
        setPickStatus(mine.status === "unset" ? "" : mine.status);
        setPickUntil(mine.until ? mine.until.slice(0, 10) : "");
        setPickNote(mine.note || "");
      }
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't load availability.");
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [teamId, userId]);

  const save = async () => {
    setSavingMine(true);
    try {
      const untilIso = pickUntil ? new Date(pickUntil + "T00:00:00Z").toISOString() : "";
      await setMyAvailability({
        status: pickStatus,
        until:  untilIso,
        note:   pickNote,
      });
      onToast?.("Availability saved.");
      await reload();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't save.");
    } finally { setSavingMine(false); }
  };

  if (!data) {
    return <p className="text-[12px] text-white/55" data-testid="team-hq-availability-loading">Loading…</p>;
  }
  const { rows = [], summary = { available: 0, limited: 0, unavailable: 0, unset: 0 } } = data;
  const total = rows.length || 1;
  return (
    <div className="space-y-4" data-testid="team-hq-availability-tab">
      {/* My status editor */}
      <div
        className="rounded-2xl border border-[rgba(20,184,166,0.30)] bg-black/30 p-4"
        data-testid="team-hq-my-availability"
      >
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-semibold mb-3">
          YOUR STATUS
        </p>
        <div className="flex flex-wrap gap-2 mb-3">
          {["available", "limited", "unavailable", ""].map((s) => {
            const cfg = STATUS_CHIP[s || "unset"];
            const active = pickStatus === s;
            return (
              <button
                key={s || "clear"}
                type="button"
                onClick={() => setPickStatus(s)}
                data-testid={`team-hq-avail-pick-${s || "clear"}`}
                className={`text-[10px] font-mono px-2.5 py-1.5 rounded-md transition-colors border ${
                  active
                    ? "bg-[#14b8a6]/20 text-[#14b8a6] border-[#14b8a6]/60"
                    : "border-[rgba(20,184,166,0.20)] text-white/70 hover:text-white"
                }`}
              >
                <i className={`fas ${cfg.icon} mr-1`} />{cfg.label}
              </button>
            );
          })}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div>
            <p className="text-[10px] tracking-widest text-white/55 mb-1">BACK BY (OPTIONAL)</p>
            <input
              type="date"
              value={pickUntil}
              onChange={(e) => setPickUntil(e.target.value)}
              disabled={!pickStatus}
              data-testid="team-hq-avail-until"
              className="w-full bg-black/30 border border-[rgba(20,184,166,0.25)] focus:border-[#14b8a6]/60 focus:outline-none rounded-lg px-3 py-2 text-sm text-white disabled:opacity-50"
            />
          </div>
          <div>
            <p className="text-[10px] tracking-widest text-white/55 mb-1">NOTE (OPTIONAL)</p>
            <input
              type="text"
              value={pickNote}
              maxLength={120}
              placeholder="e.g. on a school trip, back Mar 3"
              onChange={(e) => setPickNote(e.target.value)}
              disabled={!pickStatus}
              data-testid="team-hq-avail-note"
              className="w-full bg-black/30 border border-[rgba(20,184,166,0.25)] focus:border-[#14b8a6]/60 focus:outline-none rounded-lg px-3 py-2 text-sm text-white placeholder-white/35 disabled:opacity-50"
            />
          </div>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={savingMine}
          data-testid="team-hq-avail-save"
          className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-neon disabled:opacity-60"
        >
          {savingMine ? "SAVING…" : "SAVE STATUS"}
        </button>
      </div>

      {/* Roster matrix */}
      <div
        className="rounded-2xl border border-[rgba(20,184,166,0.25)] bg-black/25 p-4"
        data-testid="team-hq-avail-matrix"
      >
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-semibold">
            ROSTER AVAILABILITY · {rows.length}
          </p>
          <div className="flex items-center gap-1 flex-wrap">
            {["available", "limited", "unavailable", "unset"].map((k) => {
              const cfg = STATUS_CHIP[k];
              return (
                <span
                  key={k}
                  data-testid={`team-hq-avail-sum-${k}`}
                  className="text-[10px] font-mono px-2 py-0.5 rounded-md"
                  style={{
                    backgroundColor: `${cfg.color}1f`,
                    border: `1px solid ${cfg.border}55`,
                    color: cfg.color,
                  }}
                >
                  {summary[k]} {cfg.label}
                </span>
              );
            })}
          </div>
        </div>
        {/* Bar visualisation — single muted steel-blue scale, no rainbow. */}
        <div className="h-1.5 w-full rounded-full bg-black/60 overflow-hidden flex mb-3">
          {(["available", "limited", "unavailable", "unset"]).map((k) => {
            const w = (summary[k] / total) * 100;
            if (!w) return null;
            const opacity = k === "available" ? 1 : k === "limited" ? 0.55 : k === "unavailable" ? 0.30 : 0.12;
            return (
              <div
                key={k}
                style={{ width: `${w}%`, backgroundColor: "#14b8a6", opacity }}
                title={`${k} · ${summary[k]}`}
              />
            );
          })}
        </div>
        <div className="space-y-1.5">
          {rows.map((r) => {
            const cfg = STATUS_CHIP[r.status] || STATUS_CHIP.unset;
            return (
              <div
                key={r.user_id}
                data-testid={`team-hq-avail-row-${r.user_id}`}
                className="flex items-center gap-3 rounded-lg border border-[rgba(20,184,166,0.15)] bg-black/30 px-3 py-2"
              >
                <AvatarChip avatarId={r.avatar_id} pfpUrl={r.pfp_url} size={32} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-[12.5px] font-semibold text-white truncate">{r.name}</p>
                    {r.is_owner && <span className="text-[9px] tracking-widest text-[#14b8a6]/80">OWNER</span>}
                    {!r.is_owner && r.is_captain && <span className="text-[9px] tracking-widest text-[#14b8a6]/60">CAPTAIN</span>}
                  </div>
                  {r.note && (
                    <p className="text-[10.5px] text-white/55 truncate">{r.note}</p>
                  )}
                  {r.until && (
                    <p className="text-[10px] text-white/45 truncate">
                      back by {String(r.until).slice(0, 10)}
                    </p>
                  )}
                </div>
                <span
                  className="text-[10px] font-mono px-2 py-0.5 rounded-md shrink-0"
                  style={{
                    backgroundColor: `${cfg.color}1f`,
                    border: `1px solid ${cfg.border}55`,
                    color: cfg.color,
                  }}
                  data-testid={`team-hq-avail-status-${r.user_id}`}
                >
                  <i className={`fas ${cfg.icon} mr-1`} />{cfg.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
